import { type FC, useMemo } from 'react';
import NumberBall from '@/components/base/NumberBall';
import FilterBadge, { EmptyFilterState } from './FilterBadge';
import { useStatisticsData } from '@/pages/statistics/StatisticsDataContext';

const RecentDraws: FC = () => {
  const { filteredDraws, loading, advancedFilters, resetFilters } = useStatisticsData();
  const recentDraws = filteredDraws.slice(0, 20);

  const filterKey = useMemo(() => {
    return `${advancedFilters.numberRanges.join(',')}|${advancedFilters.dayOfWeeks.join(',')}|${advancedFilters.prizeRange}`;
  }, [advancedFilters]);

  if (loading) {
    return (
      <div>
        <div className="h-6 bg-background-200 rounded w-32 mb-5 animate-pulse"></div>
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-12 bg-background-200 rounded-md animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (filteredDraws.length === 0) {
    return (
      <div>
        <div className="flex items-center gap-2.5 mb-5 flex-wrap">
          <h2 className="font-heading text-xl text-foreground-900">최근 당첨번호</h2>
          <FilterBadge advancedFilters={advancedFilters} onReset={resetFilters} />
        </div>
        <EmptyFilterState />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2.5 mb-5 flex-wrap">
        <h2 className="font-heading text-xl text-foreground-900">최근 당첨번호</h2>
        <FilterBadge advancedFilters={advancedFilters} onReset={resetFilters} />
      </div>

      <div key={filterKey} className="animate-[contentFadeIn_0.35s_ease-out]">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-200/60">
                <th className="text-left py-3 px-4 text-xs font-medium text-foreground-500">회차</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-foreground-500">추첨일</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-foreground-500">당첨번호</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-foreground-500">보너스</th>
              </tr>
            </thead>
            <tbody>
              {recentDraws.map((draw, idx) => (
                <tr
                  key={draw.id}
                  className={`border-b border-background-100 transition-colors duration-150 hover:bg-background-100/50 ${idx === 0 ? 'bg-accent-50/40' : ''}`}
                >
                  <td className="py-3 px-4">
                    <span className={`font-bold text-sm ${idx === 0 ? 'text-accent-600' : 'text-foreground-800'}`}>
                      {draw.id}회
                    </span>
                    {idx === 0 && (
                      <span className="ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-bold bg-accent-500 text-accent-950">
                        NEW
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-foreground-400 text-xs">{draw.draw_date}</td>
                  <td className="py-3 px-4">
                    <div className="flex flex-wrap gap-1.5">
                      {draw.numbers.map((num) => (
                        <NumberBall key={num} number={num} size="sm" />
                      ))}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <NumberBall number={draw.bonus} size="sm" isBonus />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="md:hidden space-y-3">
          {recentDraws.map((draw, idx) => (
            <div
              key={draw.id}
              className={`rounded-xl p-4 border ${idx === 0 ? 'bg-accent-50/40 border-accent-200/60' : 'bg-background-100/80 border-background-200/60'}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-sm text-foreground-800">{draw.id}회</span>
                  {idx === 0 && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-accent-500 text-accent-950">NEW</span>
                  )}
                </div>
                <span className="text-xs text-foreground-400">{draw.draw_date}</span>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {draw.numbers.map((num) => (
                  <NumberBall key={num} number={num} size="sm" />
                ))}
                <span className="text-xs text-foreground-300 mx-1">+</span>
                <NumberBall number={draw.bonus} size="sm" isBonus />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RecentDraws;